/**
 * transitionConfig.js
 *
 * Scroll-driven background transition choreography for the Shimmering Prism. These values turn the
 * section changes from a single uniform crossfade into intentional, reactive, physically-motivated
 * moments:
 *
 *   Hero → Projects  the closed emblem ruptures — the spin ramps up smoothly, opens while spinning,
 *                    then COASTS DOWN under friction (inertia, not a brake).
 *   Projects → Hero  the mirror of the rupture (scroll-up): the spin ramps up, the form CLOSES back to
 *                    the Hero emblem while spinning, then the spin coasts down. Fully visible (no mask),
 *                    so it uses the energetic Hero→Projects timing, not the stretched About→Hero onset.
 *   Projects → Lab   no open/close event — parallax/scroll pan only (the presets are identical).
 *   Lab → About      stays open; a weighted, inertial camera glide into the close-up (accelerate in,
 *                    decelerate softly near the target) — not a linear A→B slide.
 *   About → Hero     reconstitution (fires on the hidden bottom-scroll loop or any About→Hero nav):
 *                    spin ramps up, the form closes + pulls back + recenters, then the spin coasts down.
 *
 * MOTION MODEL — the spin is velocity/inertia based, not a value tweened A→B. A GSAP tween ramps the
 * angular velocity (_spinVel) up to a peak (a smooth S-curve, no instant jump); when the accelerator is
 * released the velocity DECAYS each frame through friction (_spinVel *= spinDamping per 60fps frame) and
 * is clamped to 0 only below minSpinVelocityCutoff. ShimmeringPrism integrates it (_spinPhase += vel·dt)
 * inside the existing render loop — no second RAF, no hard zeroing, no power4.in brake. See _buildTransition.
 *
 * All timing/easing/damping lives here so it can be tuned in one place (no scattered magic numbers).
 * Durations are seconds; eases are GSAP ease names; damping is a per-frame (at 60fps) multiplier applied
 * frame-rate-independently. The look TARGETS (open vs closed, framing, brightness) are the SECTION_PRESETS
 * in ShimmeringPrism.js — this file only governs HOW the look moves between them.
 *
 * Tuning knobs (visual): `maxSpinSpeed` (peak spin), `spinDamping` (lower = quicker coast; higher = longer
 * glide), and the camera `cameraEase`/`cameraApproachDuration` for the Lab→About immersion.
 */

/** Fallback for transitions without bespoke choreography (reverse nav, menu jumps). Matches the
 *  prior section crossfade (duration 1, power4.out). Any residual burst spin simply coasts down. */
export const SECTION_TRANSITION_DEFAULT = {
    duration: 1.0,
    ease: 'power4.out',
};

export const SECTION_TRANSITIONS = {
    // Hero (closed, centered) → Projects (opened, atmospheric). Also used for any Hero→opened jump.
    // The spin ramps up smoothly toward maxSpinSpeed, the form opens after a short delay while still
    // spinning, then the accelerator is released and the spin COASTS DOWN under friction (no brake).
    heroToProjects: {
        spinAccelerationDuration: 0.55,  // s — smooth velocity ramp 0 → maxSpinSpeed (no instant jump)
        spinAccelEase: 'power2.inOut',   // S-curve ramp (eases up to peak, not a kick)
        maxSpinSpeed: 5.0,               // rad/s peak velocity (tune visually — higher = faster spin)
        // Outro friction. Close to 1 = a LONG, gentle release (it sheds speed, never brakes): at 0.98 the
        // velocity halves every ~0.57s and the perceptible spin-down lasts ~4s before easing to rest. This
        // is the headline "smoothness" knob — lower (e.g. 0.96) for a quicker stop, higher (→0.99) for an
        // even longer glide. (Was 0.94 ≈ a ~0.19s half-life, which read as braking.)
        spinDamping: 0.98,
        minSpinVelocityCutoff: 0.002,    // rad/s — below this the coast snaps to 0 (imperceptible)
        openDelay: 0.50,                 // s — begin opening this long after the spin starts
        openDuration: 1.0,               // s — the opening itself (overture → target)
        openEase: 'power3.inOut',        // organic, non-linear
        paramDuration: 1.0,              // s — framing/brightness/etc. settle (everything except opening)
        paramEase: 'power3.inOut',
    },

    // Projects (opened) → Hero (closed, centered), scroll-up. The mirror of heroToProjects: the spin ramps
    // up smoothly, the form CLOSES back to the Hero emblem after a short delay while still spinning, then
    // the accelerator is released and the spin COASTS DOWN under friction. Fully visible (no loop mask), so
    // it uses the energetic heroToProjects timing rather than the stretched aboutToHero onset.
    projectsToHero: {
        spinAccelerationDuration: 0.55,  // s — same energetic ramp as the forward rupture
        spinAccelEase: 'power2.inOut',
        maxSpinSpeed: 5.0,               // rad/s — match heroToProjects so the return feels equivalent
        spinDamping: 0.98,               // same long, gentle release (~0.57s half-life, ~4s perceptible)
        minSpinVelocityCutoff: 0.002,
        closeDelay: 0.50,                // s — begin closing this long after the spin starts (mirrors openDelay)
        closeDuration: 1.0,              // s — the closing itself (overture → home)
        closeEase: 'power3.inOut',
        paramDuration: 1.0,              // s — framing/brightness/etc. settle (everything except the close)
        paramEase: 'power3.inOut',
    },

    // Projects → Lab: a gentle descent into the nebula. The lab preset now diverges from deep/projects
    // (cameraDist 85→95, timeSpeed 0.01→0.008 — see SECTION_PRESETS.lab), so this single weighted settle
    // expresses the depth move: the field eases back + the drift calms. Slower in/out ease so it reads as
    // sinking deeper, not a vertical slide. No spin/open event (calmer than the HOME rupture, by design).
    projectsToLab: {
        duration: 1.3,
        ease: 'power2.inOut',
    },

    // Lab → About: the form stays open; the camera glides closer with weight. The cameraDist approach is
    // tweened separately with a strong inertial ease (slow start → accelerate → decelerate softly into the
    // close-up) over a longer duration, while brightness/motion fade down gradually. Reads as "entering"
    // the opened system, not a linear slide.
    labToAbout: {
        cameraApproachDuration: 1.9,     // s — the weighted camera glide (cameraDist 95 → 18, into the core)
        cameraEase: 'expo.inOut',        // pronounced accelerate-in / decelerate-out — immersive depth
        duration: 1.6,                   // s — brightness / motion-speed / orbit fade (gradual)
        ease: 'power2.inOut',
    },

    // About (opened, close-up) → Hero (closed, centered): the reconstitution. The spin ramps up smoothly
    // (no jump from 0), the form closes (overture → home) and pulls back + recenters, then the spin coasts
    // down under friction into the Hero idle (no sudden stop). Tuned to read through/after the loop mask.
    aboutToHero: {
        // Gentle, lengthened ramp (longer than Hero→Projects' 0.55s) — intentionally asymmetric. The loop
        // mask covers the first ~0.3s of this transition, so a short ramp would already be ~70% spun-up
        // when the background is revealed → reads as an abrupt "pop-in already spinning." Stretching the
        // onset (+ a flatter power3.inOut toe) keeps the spin slow as it becomes visible, then it
        // accelerates smoothly: gentle IN, the same way it coasts OUT. (Lower if it feels lazy to start.)
        spinAccelerationDuration: 1.1,
        spinAccelEase: 'power3.inOut',
        maxSpinSpeed: 3.5,               // rad/s — gentler than the Hero→Projects rupture
        // Same long, gentle release as Hero→Projects: ~0.57s half-life, ~3.5s perceptible spin-down that
        // coasts into the Hero idle instead of braking. Lower (e.g. 0.96) to settle into Hero sooner.
        spinDamping: 0.98,
        minSpinVelocityCutoff: 0.002,
        // Close + recenter are slow and weighted (longer than a forward section change). Like the spin,
        // they start under the loop mask (~0.3s hidden), so a 1s tween would be ripping through its fast
        // power3.inOut middle at reveal; stretching them keeps the form contraction + camera pull-back
        // gentle and unhurried when it becomes visible. (Lower both together for a quicker reset.)
        closeDuration: 1.6,              // s — overture eases back to the closed Hero value
        closeEase: 'power3.inOut',
        recenterDuration: 1.7,           // s — framing/brightness/etc. ease back to Hero (pull back + recenter)
        recenterEase: 'power3.inOut',
    },
};
